import { defineStore } from 'pinia';
import { ref } from 'vue';
import activitiesAPI from '@/api/activities';
import type { Activity } from '@/types/Activity';

export const useActivitiesStore = defineStore('activities', () => {
  const activities = ref<Activity[]>([]);
  const activity = ref<Activity | null>(null);
  
  const fetchActivities = async (params: any): Promise<{ data: Activity[], total: number }> => {
    try {
      const response = await activitiesAPI.getActivities(params);
      const data: Activity[] = response.data.data;
      const total = response.data.meta.total;
      activities.value = data;
      return { data, total };
    } catch (error) {
      console.error('Error fetching activities:', error);
      throw error;
    }
  };

  const fetchActivityById = async (id: number) => {
    try {
      const response = await activitiesAPI.getActivityById(id);
      activity.value = response.data.data as Activity;
      return response.data.data as Activity;
    } catch (error) {
      console.error(`Error fetching activity  with ID ${id}:`, error);
      throw error;
    }
  };

  const createActivity = async (activityData: Partial<Activity>) => {
    try {
      const response = await activitiesAPI.createActivity(activityData);
      if (!response.data.success) {
        throw response.data;
      }
      return response.data;
    } catch (error) {
      console.error('Error creating activity :', error);
      throw error;
    }
  };

  const updateActivity = async (id: number, activityData: Partial<Activity>) => {
    try {
      const response = await activitiesAPI.updateActivity(id, activityData);
      if (!response.data.success) {
        throw response.data;
      }
      const updated = response.data as Activity;
      const index = activities.value.findIndex((c:any) => c.id === id);
      if (activity.value?.id === id) activity.value = updated;
      if (index !== -1) {
        activities.value[index] = updated;
      }
      return response.data;
    } catch (error) {
      console.error(`Error updating activity  with ID ${id}:`, error);
      throw error;
    }
  };

  const deleteActivity = async (id: number) => {
    try {
      await activitiesAPI.deleteActivity(id);
      activities.value = activities.value.filter((c) => c.id !== id);
    } catch (error) {
      console.error(`Error deleting activity  with ID ${id}:`, error);
      throw error;
    }
  };

  const rescheduleActivity = async (id: number, payload: any) => {
    try {
      // payload: los datos que pide el back
      const response = await activitiesAPI.rescheduleActivity(id, payload);
      if (!response.data.success) {
        throw response.data;
      }
      
      return response.data;
    } catch (e) {
      throw e;
    }
  };

  const exportCSV = async (params: string, body: any) => {
    try {
      return await activitiesAPI.exportCSV(params, body);
    } catch (error) {
      console.error('Error exportando archivo:', error);
      throw error;
    }
  };

  return {
    activities,
    activity,
    fetchActivities,
    fetchActivityById,
    createActivity,
    updateActivity,
    deleteActivity,
    rescheduleActivity,
    exportCSV
  };
});
